// Task progress bar component with smooth animations
// CREATED: Visual indicator showing completion progress of daily tasks
// Features: smooth transitions, milestone celebrations, responsive design
// UPDATED: Removed Clock icon, changed "Progression" to "Objectif du jour"
// UPDATED: Increased bar height for better visibility and added progressive fill animation from 0%

import { CheckCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

interface TaskProgressBarProps {
  completed: number;
  total: number;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  animated?: boolean;
}

export function TaskProgressBar({
  completed,
  total,
  showLabel = true,
  size = 'md',
  animated = true
}: TaskProgressBarProps) {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [justCompleted, setJustCompleted] = useState(false);

  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isComplete = completed === total && total > 0;

  // Animate progress on mount and when values change
  useEffect(() => {
    if (animated) {
      // Start from 0 for progressive fill animation
      setDisplayProgress(0);
      // Delay start for smooth entrance
      const timer = setTimeout(() => {
        setDisplayProgress(percentage);
      }, 150);
      return () => clearTimeout(timer);
    } else {
      setDisplayProgress(percentage);
    }
  }, [percentage, animated]);

  // Trigger celebration animation when all tasks are completed
  useEffect(() => {
    if (isComplete && percentage === 100) {
      setJustCompleted(true);
      const timer = setTimeout(() => setJustCompleted(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isComplete, percentage]);

  const getHeightClass = () => {
    switch (size) {
      case 'sm': return 'h-2';
      case 'md': return 'h-3';
      case 'lg': return 'h-4';
    }
  };

  const getTextSizeClass = () => {
    switch (size) {
      case 'sm': return 'text-xs';
      case 'md': return 'text-sm';
      case 'lg': return 'text-base';
    }
  };

  const getProgressColor = () => {
    if (isComplete) return 'bg-green-500';
    if (percentage >= 75) return 'bg-primary-500';
    if (percentage >= 50) return 'bg-primary-400';
    if (percentage >= 25) return 'bg-primary-300';
    return 'bg-gray-300';
  };

  return (
    <div className="w-full">
      {/* Label and Stats */}
      {showLabel && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className={`font-medium text-gray-700 ${getTextSizeClass()}`}>
              Objectif du jour
            </span>
          </div>
          <span className={`font-bold ${isComplete ? 'text-green-700' : 'text-gray-700'} ${getTextSizeClass()}`}>
            {completed} / {total}
          </span>
        </div>
      )}

      {/* Progress Bar Container */}
      <div className="relative w-full bg-gray-200 rounded-full overflow-hidden shadow-inner">
        {/* Animated Background Shine (when in progress) */}
        {!isComplete && displayProgress > 0 && displayProgress < 100 && (
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
              animation: 'shimmer 2s infinite',
            }}
          />
        )}

        {/* Progress Fill */}
        <div
          className={`${getHeightClass()} ${getProgressColor()} rounded-full transition-all duration-1000 ease-out relative overflow-hidden ${
            justCompleted ? 'animate-pulse' : ''
          }`}
          style={{
            width: `${displayProgress}%`,
            boxShadow: displayProgress > 0 ? '0 0 10px rgba(59, 130, 246, 0.5)' : 'none'
          }}
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Progression: ${completed} sur ${total} missions terminées`}
        >
          {/* Inner Glow Effect */}
          {displayProgress > 5 && (
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20"
              style={{
                animation: 'slideRight 2s ease-in-out infinite'
              }}
            />
          )}
        </div>

        {/* Milestone Markers (25%, 50%, 75%) */}
        {total > 4 && (
          <>
            {[25, 50, 75].map((milestone) => (
              <div
                key={milestone}
                className={`absolute top-0 ${getHeightClass()} w-0.5 bg-white/40 transition-opacity duration-300 ${
                  displayProgress >= milestone ? 'opacity-0' : 'opacity-100'
                }`}
                style={{ left: `${milestone}%` }}
                aria-hidden="true"
              />
            ))}
          </>
        )}
      </div>

      {/* Percentage Text (optional small display) */}
      {size === 'lg' && (
        <div className="text-center mt-2">
          <span className={`text-xs font-medium ${isComplete ? 'text-green-600' : 'text-gray-500'}`}>
            {percentage}%
          </span>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes slideRight {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
      `}</style>
    </div>
  );
}
